import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { AlertTriangle, RefreshCw, Home, ArrowLeft } from 'lucide-react';
import { toast } from '@/hooks/use-toast';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  errorId: string;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: ''
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    // Update state so the next render will show the fallback UI
    return {
      hasError: true,
      error,
      errorId: `error_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log error details
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    
    this.setState({
      error,
      errorInfo
    });

    // Call optional error handler
    this.props.onError?.(error, errorInfo);

    // Show toast notification
    toast({
      title: "Application Error",
      description: "An unexpected error occurred. The error has been logged.",
      variant: "destructive",
    });

    // Send error to monitoring service (if you have one)
    this.logErrorToService(error, errorInfo);
  }

  logErrorToService = (error: Error, errorInfo: ErrorInfo) => {
    // In production, you would send this to your error monitoring service
    // like Sentry, LogRocket, etc.
    const errorData = {
      message: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href,
      errorId: this.state.errorId,
      // Performance metrics
      memory: (performance as unknown as { memory?: { usedJSHeapSize: number; totalJSHeapSize: number; jsHeapSizeLimit: number } })?.memory ? {
        used: (performance as unknown as { memory: { usedJSHeapSize: number } }).memory.usedJSHeapSize,
        total: (performance as unknown as { memory: { totalJSHeapSize: number } }).memory.totalJSHeapSize,
        limit: (performance as unknown as { memory: { jsHeapSizeLimit: number } }).memory.jsHeapSizeLimit
      } : undefined,
      // User context
      userId: localStorage.getItem('userId') || 'anonymous',
      sessionId: sessionStorage.getItem('sessionId') || 'unknown',
      // Error categorization
      errorType: this.categorizeError(error),
      severity: this.getErrorSeverity(error)
    };

    // For development, just log to console
    if (process.env.NODE_ENV === 'development') {
      console.group('🚨 Error Boundary Details');
      console.error('Error:', error);
      console.error('Error Info:', errorInfo);
      console.error('Error Data:', errorData);
      console.groupEnd();
    }

    // In production, send to your monitoring service:
    if (process.env.NODE_ENV === 'production') {
      // Example integrations (uncomment the one you use):
      
      // Sentry
      // import * as Sentry from '@sentry/react';
      // Sentry.captureException(error, { 
      //   extra: errorData,
      //   tags: { errorBoundary: true, severity: errorData.severity }
      // });
      
      // Custom API endpoint
      // fetch('/api/errors', {
      //   method: 'POST',
      //   headers: { 'Content-Type': 'application/json' },
      //   body: JSON.stringify(errorData)
      // }).catch(console.error);
    }
  };

  // Categorize errors for better debugging
  private categorizeError = (error: Error): string => {
    const message = error.message.toLowerCase();
    const stack = error.stack?.toLowerCase() || '';
    
    if (message.includes('network') || message.includes('fetch')) return 'NETWORK';
    if (message.includes('chunk') || message.includes('loading')) return 'CHUNK_LOAD';
    if (message.includes('permission') || message.includes('unauthorized')) return 'PERMISSION';
    if (stack.includes('react') || stack.includes('jsx')) return 'REACT_RENDER';
    if (message.includes('timeout')) return 'TIMEOUT';
    if (message.includes('memory')) return 'MEMORY';
    return 'UNKNOWN';
  };

  // Determine error severity
  private getErrorSeverity = (error: Error): 'low' | 'medium' | 'high' | 'critical' => {
    const message = error.message.toLowerCase();
    
    if (message.includes('network') || message.includes('timeout')) return 'medium';
    if (message.includes('permission') || message.includes('auth')) return 'high';
    if (message.includes('memory') || message.includes('chunk')) return 'critical';
    return 'medium';
  };

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  handleGoBack = () => {
    window.history.back();
  };

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: ''
    });
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error UI
      return (
        <div className="min-h-screen bg-background flex items-center justify-center p-4">
          <Card className="w-full max-w-2xl border-destructive/20">
            <CardContent className="p-8 text-center">
              <div className="mb-6">
                <div className="w-20 h-20 bg-destructive/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <AlertTriangle className="w-10 h-10 text-destructive" />
                </div>
                <h1 className="text-2xl font-bold text-foreground mb-2">
                  Oops! Something went wrong
                </h1>
                <p className="text-muted-foreground mb-4">
                  The application encountered an unexpected error. Don't worry, your data is safe.
                </p>
                
                {process.env.NODE_ENV === 'development' && this.state.error && (
                  <details className="mt-4 p-4 bg-muted rounded-lg text-left">
                    <summary className="cursor-pointer font-medium text-sm mb-2">
                      🔍 Error Details (Development Mode)
                    </summary>
                    <div className="text-xs font-mono space-y-2">
                      <div>
                        <strong>Error:</strong> {this.state.error.message}
                      </div>
                      <div>
                        <strong>Error ID:</strong> {this.state.errorId}
                      </div>
                      {this.state.error.stack && (
                        <div>
                          <strong>Stack Trace:</strong>
                          <pre className="mt-1 overflow-auto max-h-32 text-xs">
                            {this.state.error.stack}
                          </pre>
                        </div>
                      )}
                    </div>
                  </details>
                )}
              </div>

              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button onClick={this.handleReset} variant="outline" className="gap-2">
                  <RefreshCw className="w-4 h-4" />
                  Try Again
                </Button>
                
                <Button onClick={this.handleReload} variant="outline" className="gap-2">
                  <RefreshCw className="w-4 h-4" />
                  Reload Page
                </Button>
                
                <Button onClick={this.handleGoBack} variant="outline" className="gap-2">
                  <ArrowLeft className="w-4 h-4" />
                  Go Back
                </Button>
                
                <Button onClick={this.handleGoHome} className="gap-2">
                  <Home className="w-4 h-4" />
                  Go to Home
                </Button>
              </div>

              <div className="mt-6 text-xs text-muted-foreground">
                <p>If this problem persists, please contact support.</p>
                {this.state.errorId && (
                  <p className="mt-1">Error ID: <code className="bg-muted px-1 rounded">{this.state.errorId}</code></p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary; 